/**
 * Evaluation Page
 *
 * Main page for running evaluations and collecting human feedback.
 *
 * Flow:
 * 1. User enters dataset name + negotiation ID
 * 2. Click "Start Evaluation"
 * 3. Show progress bar (poll every 2 seconds)
 * 4. When complete, show results in cards
 * 5. User provides thumbs up/down + notes for each result
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvaluationStore } from '../../stores/evaluationStore';
import { EvaluationAPI, EvaluationRunStatus } from '../../api/evaluation';
import { FormSection } from './FormSection';
import { ProgressSection } from './ProgressSection';
import { ResultsSection } from './ResultsSection';
import { Dropdown } from 'primereact/dropdown';

export const Evaluation: React.FC = () => {
  const navigate = useNavigate();
  const {
    runStatus,
    results,
    polling,
    stopPolling,
    reset,
  } = useEvaluationStore();

  const [recentRuns, setRecentRuns] = useState<EvaluationRunStatus[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(true);

  // Fetch recent evaluation runs on mount
  useEffect(() => {
    const fetchRecentRuns = async () => {
      try {
        setLoadingRuns(true);
        const runs = await EvaluationAPI.listRecentRuns(20);
        setRecentRuns(runs);
      } catch (error) {
        console.error('Failed to fetch recent runs:', error);
      } finally {
        setLoadingRuns(false);
      }
    };

    fetchRecentRuns();
  }, []);

  // Cleanup: stop polling when component unmounts
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Evaluation Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Run evaluations on datasets and provide human feedback on results
        </p>

        {/* Show run info when active */}
        {runStatus && (
          <div className="mt-4 flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700">Dataset:</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                {runStatus.dataset_name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700">Negotiation ID:</span>
              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded font-mono">
                {runStatus.negotiation_id}
              </span>
            </div>
          </div>
        )}

        {/* Historical Runs Dropdown */}
        {!runStatus && recentRuns.length > 0 && (
          <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              View Previous Evaluation Runs
            </label>
            <Dropdown
              value={null}
              options={recentRuns.map(run => ({
                label: `${run.dataset_name} (Neg ${run.negotiation_id}) - ${new Date(run.created_at).toLocaleString()} - ${run.status}`,
                value: run.run_id
              }))}
              onChange={(e) => navigate(`/evaluation/runs/${e.value}`)}
              placeholder={loadingRuns ? 'Loading...' : 'Select a previous run to view'}
              className="w-full"
              disabled={loadingRuns}
            />
          </div>
        )}
      </div>

      {/* Form Section - Show when no run is active */}
      {!runStatus && <FormSection />}

      {/* Progress Section - Show while evaluation is running */}
      {polling && runStatus && <ProgressSection status={runStatus} />}

      {/* Results Section - Show when evaluation is completed */}
      {!polling && runStatus?.status === 'COMPLETED' && (
        <div>
          {results.length > 0 ? (
            <ResultsSection results={results} />
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-yellow-800 mb-2">No Results Available</h2>
              <p className="text-yellow-700 mb-4">
                The evaluation completed but no results were returned. This may indicate an issue with fetching traces from Langfuse.
              </p>
              <p className="text-sm text-yellow-600">
                Check the browser console and backend logs for more details.
              </p>
            </div>
          )}

          {/* Reset Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={reset}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Start New Evaluation
            </button>
          </div>
        </div>
      )}

      {/* Failed State */}
      {runStatus?.status === 'FAILED' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Evaluation Failed</h2>
          <p className="text-red-700 mb-4">{runStatus.error_message}</p>
          <button
            onClick={reset}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};
